import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer, List, ListItemButton, ListItemText, ListItemIcon,
    Box, Typography, Avatar, Button, Tooltip,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';

import StorageIcon          from '@mui/icons-material/Storage';
import BarChartIcon         from '@mui/icons-material/BarChart';
import GroupIcon            from '@mui/icons-material/Group';
import TuneIcon             from '@mui/icons-material/Tune';
import ShieldIcon           from '@mui/icons-material/Shield';
import ForumIcon            from '@mui/icons-material/Forum';
import SupportAgentIcon     from '@mui/icons-material/SupportAgent';
import ArticleIcon          from '@mui/icons-material/Article';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AccountTreeIcon      from '@mui/icons-material/AccountTree';
import SmartToyIcon         from '@mui/icons-material/SmartToy';
import HelpCenterIcon       from '@mui/icons-material/HelpCenter';
import SmartDisplayIcon     from '@mui/icons-material/SmartDisplay';
import LockIcon             from '@mui/icons-material/Lock';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useAccess } from '../context/AccessProvider';
import chatbotService from '../services/ChatbotService';
import netbotLogo from '../assets/netbot-logo-wide.png';

// ─── palette ────────────────────────────────────────────────────────────────
const C = {
    bg:           '#f6f6f6',
    bgHover:      '#eeeeee',
    bgActive:     '#e8e8e8',
    border:       '#dddddd',
    text:         '#303030',
    textMuted:    '#6f6f6f',
    icon:         '#737373',
    iconActive:   '#666666',
    avatarBg:     '#d9d9d9',
    linkBlue:     '#1976d2',
    logoutBg:     '#202020',
    logoutHover:  '#111111',
};

const SIDEBAR_W = 300;

// ─── helpers ────────────────────────────────────────────────────────────────
const NavItem = ({ to, icon, label, active, locked = false, lockedLabel = 'Upgrade Required' }) => {
    const content = (
        <ListItemButton
            component={locked ? 'div' : Link}
            to={locked ? undefined : to}
            sx={{
                mx: 0,
                px: 2.4,
                py: 1.3,
                minHeight: 58,
                borderRadius: 0,
                color: locked ? '#8b8b8b' : (active ? C.text : C.textMuted),
                backgroundColor: !locked && active ? C.bgActive : 'transparent',
                cursor: locked ? 'default' : 'pointer',
                transition: 'all 0.18s ease',
                '&:hover': {
                    backgroundColor: locked ? 'transparent' : (active ? C.bgActive : C.bgHover),
                    color: locked ? '#8b8b8b' : C.text,
                },
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 64,
                    color: locked ? '#8b8b8b' : (active ? C.iconActive : C.icon),
                    transition: 'color 0.18s ease',
                    '& .MuiSvgIcon-root': {
                        fontSize: 30,
                    },
                }}
            >
                {icon}
            </ListItemIcon>
            <ListItemText
                primary={label}
                primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: active ? 600 : 400,
                    letterSpacing: 0,
                    lineHeight: 1.4,
                    whiteSpace: 'normal',
                }}
            />
            {locked && (
                <LockIcon
                    sx={{
                        ml: 1,
                        mr: 0.2,
                        fontSize: 22,
                        color: '#9a9a9a',
                        flexShrink: 0,
                    }}
                />
            )}
        </ListItemButton>
    );

    return (
        <Tooltip
            title={locked ? lockedLabel : label}
            placement="right"
            arrow
            componentsProps={locked ? {
                tooltip: {
                    sx: {
                        bgcolor: '#d32f2f',
                        color: '#ffffff',
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        lineHeight: 1.35,
                        px: 1.8,
                        py: 1,
                    },
                },
                arrow: {
                    sx: {
                        color: '#d32f2f',
                    },
                },
            } : undefined}
        >
            {content}
        </Tooltip>
    );
};

// ─── main component ──────────────────────────────────────────────────────────
const Sidebar = ({ userName, group, loginAs, onLogout, onLogoutAs }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const currentGroup = String(group ?? '');

    // Plan features + permission-group permissions come from AccessProvider.
    const { can, hasFeature, planUpgradeable, planName } = useAccess();

    // Client-side people: client owner, its users (members), or admin acting-as.
    const isClientSide = currentGroup === 'customer' || currentGroup === 'member' || Boolean(loginAs);
    const netbot = hasFeature('nebot.support') || hasFeature('netbot.support');

    // Each nav gate = (permission group grants it) AND (plan includes it).
    const canSeeDataManagement       = isClientSide && can('kb') && hasFeature('voiceflow.support');
    const canSeeDocuments            = isClientSide && can('kb') && netbot;
    const canSeeConversations        = isClientSide && can('conversations') && netbot;
    const canSeeAnalytics            = isClientSide && can('analytics') && netbot;
    const canSeeChatbotConfiguration = isClientSide && can('chatbots') && hasFeature('chatbot.chatbot');
    const canSeeAgentConfiguration   = isClientSide && can('agents') && hasFeature('chatbot.agent');
    const canSeeVoiceBots = isClientSide && can('agents') && hasFeature('voice_enable');
    const canSeeAgentRequests        = isClientSide && can('livechat') && (hasFeature('system.agent_requests') || hasFeature('chatbot.live_agent'));
    const canSeeTickets              = isClientSide && can('tickets') && hasFeature('system.ticketing');
    const canSeeMembers              = isClientSide && can('members');

    const featureLockedLabel = t('sideBar.upgradeRequired', 'Upgrade Required');
    // Only render what the person may actually access (no plan-upsell locks).
    const shouldRenderFeature = (enabled) => enabled;
    const shouldLockFeature = () => false;
    const isRootPath = location.pathname === '/';

    const active = (path) => {
        if (location.pathname === path || location.pathname.startsWith(`${path}/`)) return true;
        if (path === '/data-management' && isRootPath && (canSeeDataManagement || currentGroup === 'member')) return true;
        if (path === '/documents' && (active('/kbases') || (isRootPath && !canSeeDataManagement))) return true;
        if (path === '/customer-list' && isRootPath && currentGroup === 'admin' && loginAs === null) return true;
        if (path === '/data-management' && Boolean(loginAs) && location.pathname === '/customer-list') return true;
        if (path === '/customer-list' && location.pathname.startsWith('/admin/clients/')) return true;
        return false;
    };

    const initials = userName
        ? userName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()
        : '?';

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: SIDEBAR_W,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: SIDEBAR_W,
                    boxSizing: 'border-box',
                    position: 'fixed',
                    height: '100vh',
                    top: 0,
                    zIndex: 1000,
                    backgroundColor: C.bg,
                    borderRight: `1px solid ${C.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'hidden',
                },
            }}
        >
            {/* ── Brand header ─────────────────────────────────────── */}
            <Box
                sx={{
                    px: 2.25,
                    pt: 1.35,
                    pb: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <img
                    src={netbotLogo}
                    alt="NetBot"
                    style={{ height: 38, width: 'auto', objectFit: 'contain', display: 'block' }}
                />
            </Box>

            {/* ── User profile ─────────────────────────────────────── */}
            <Box
                sx={{
                    px: 2,
                    pt: 0.8,
                    pb: 2,
                    borderBottom: `1px solid ${C.border}`,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.4,
                }}
            >
                <Avatar
                    sx={{
                        bgcolor: C.avatarBg,
                        color: '#ffffff',
                        width: 52,
                        height: 52,
                        fontSize: '1.15rem',
                        fontWeight: 400,
                        flexShrink: 0,
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography
                        noWrap
                        sx={{ fontSize: '1rem', fontWeight: 700, color: C.text, lineHeight: 1.25 }}
                    >
                        {userName || '—'}
                    </Typography>
                    {loginAs ? (
                        <Typography noWrap sx={{ mt: 0.35, fontSize: '0.78rem', color: C.textMuted }}>
                            {t('welcomeBar.actingAs', 'Acting as: ')}{loginAs}
                        </Typography>
                    ) : null}
                    {planName ? (
                        <Typography noWrap sx={{ mt: 0.35, fontSize: '0.78rem', color: C.textMuted, fontWeight: 500 }}>
                            {planName}
                        </Typography>
                    ) : null}
                </Box>
            </Box>

            {/* ── Navigation ───────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.6 }}>
                <List disablePadding>
                    {/* ── Client owner / User / LoginAs ── */}
                    {isClientSide && (
                        <>
                            {canSeeDataManagement && (
                                <NavItem to="/data-management" icon={<StorageIcon />}
                                    label={t('sideBar.dataManagement', 'Data')} active={active('/data-management')}
                                    locked={false} lockedLabel={featureLockedLabel} />
                            )}
                            {shouldRenderFeature(canSeeDocuments) && (
                                <NavItem to="/documents" icon={<ArticleIcon />}
                                    label={t('sideBar.documents', 'Documents')} active={active('/documents') || active('/chatbots')}
                                    locked={shouldLockFeature(canSeeDocuments)} lockedLabel={featureLockedLabel} />
                            )}
                            {canSeeMembers && (
                                <NavItem to="/member-list" icon={<GroupIcon />}
                                    label={t('sideBar.memberList', 'Members')} active={active('/member-list')} />
                            )}
                            <NavItem to="/settings" icon={<ShieldIcon />}
                                label={t('sideBar.settings', 'Settings')} active={active('/settings')} />
                            {shouldRenderFeature(canSeeConversations) && (
                                <NavItem to="/nchistory" icon={<ForumIcon />}
                                    label={t('sideBar.conversationHistory', 'Conversation History')} active={active('/nchistory')}
                                    locked={shouldLockFeature(canSeeConversations)} lockedLabel={featureLockedLabel} />
                            )}
                            {(currentGroup === 'customer' || (currentGroup === 'admin' && loginAs)) && (
                                <NavItem to="/audit-logs" icon={<ShieldIcon />}
                                    label={t('auditLogs.title')} active={active('/audit-logs')} />
                            )}
                            {shouldRenderFeature(canSeeAnalytics) && (
                                <NavItem to="/nanalytics" icon={<BarChartIcon />}
                                    label={t('sideBar.nanalytics', 'Analytics')} active={active('/nanalytics')}
                                    locked={shouldLockFeature(canSeeAnalytics)} lockedLabel={featureLockedLabel} />
                            )}
                            {shouldRenderFeature(canSeeChatbotConfiguration) && (
                                <NavItem to="/chatbot-settings" icon={<SmartToyIcon />}
                                    label={t('sideBar.chatbotMenu', 'Chatbots')} active={active('/chatbot-settings')}
                                    locked={shouldLockFeature(canSeeChatbotConfiguration)} lockedLabel={featureLockedLabel} />
                            )}
                            {shouldRenderFeature(canSeeVoiceBots) && (
                                <NavItem to="/voicebots" icon={<SmartToyIcon />}
                                    label={t('voiceBots.title')} active={active('/voicebots')}
                                    locked={shouldLockFeature(canSeeVoiceBots)} lockedLabel={featureLockedLabel} />
                            )}
                            {shouldRenderFeature(canSeeAgentConfiguration) && (
                                <NavItem to="/agents" icon={<TuneIcon />}
                                    label={t('sideBar.agents', 'Agents')} active={active('/agents')}
                                    locked={shouldLockFeature(canSeeAgentConfiguration)} lockedLabel={featureLockedLabel} />
                            )}
                            {canSeeDataManagement && (
                                <NavItem to="/conversations" icon={<ForumIcon />}
                                    label={t('sideBar.conversations', 'Conversations')} active={active('/conversations')}
                                    locked={false} lockedLabel={featureLockedLabel} />
                            )}
                            {shouldRenderFeature(canSeeAgentRequests) && (
                                <NavItem to="/agent-requests" icon={<SupportAgentIcon />}
                                    label={t('sideBar.agentRequest', 'Agent Requests')} active={active('/agent-requests')}
                                    locked={shouldLockFeature(canSeeAgentRequests)} lockedLabel={featureLockedLabel} />
                            )}
                            {shouldRenderFeature(canSeeTickets) && (
                                <NavItem to="/tickets" icon={<ConfirmationNumberIcon />}
                                    label={t('sideBar.tickets', 'Tickets')} active={active('/tickets')}
                                    locked={shouldLockFeature(canSeeTickets)} lockedLabel={featureLockedLabel} />
                            )}
                            {canSeeDataManagement && (
                                <NavItem to="/usage" icon={<BarChartIcon />}
                                    label={t('sideBar.usage', 'Usage')} active={active('/usage')}
                                    locked={false} lockedLabel={featureLockedLabel} />
                            )}
                            <NavItem to="/help" icon={<HelpCenterIcon />}
                                label={t('sideBar.helpCenter', 'Help Center')} active={active('/help')} />
                        </>
                    )}

                    {/* ── Admin (no loginAs) ── */}
                    {currentGroup === 'admin' && loginAs === null && (
                        <>
                            <NavItem to="/customer-list" icon={<GroupIcon />}
                                label={t('sideBar.customerList', 'Client List')} active={active('/customer-list')} />
                            <NavItem to="/admin-setting" icon={<TuneIcon />}
                                label={t('sideBar.adminSetting', 'Admin Setting')} active={active('/admin-setting')} />
                            <NavItem to="/plans" icon={<PlaylistAddCheckIcon />}
                                label={t('sideBar.plans', 'Plans')} active={active('/plans')} />
                            <NavItem to="/permission-groups" icon={<AdminPanelSettingsIcon />}
                                label={t('sideBar.permissionGroups', 'Permission Groups')} active={active('/permission-groups')} />
                            <NavItem to="/vector-stores" icon={<AccountTreeIcon />}
                                label={t('sideBar.vectorStores', 'Vector Stores')} active={active('/vector-stores')} />
                            <NavItem to="/admin/tickets" icon={<ConfirmationNumberIcon />}
                                label={t('sideBar.adminTickets', 'Admin Tickets')} active={active('/admin/tickets')} />
                            <NavItem to="/help-admin" icon={<SmartDisplayIcon />}
                                label={t('sideBar.helpCenterManage', 'Help Center Management')} active={active('/help-admin')} />
                            <NavItem to="/settings" icon={<ShieldIcon />}
                                label={t('sideBar.settings', 'Authentication Settings')} active={active('/settings')} />
                        </>
                    )}
                </List>
            </Box>

            {/* ── Footer ───────────────────────────────────────────── */}
            <Box
                sx={{
                    px: 0,
                    py: 1.5,
                    borderTop: `1px solid ${C.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                }}
            >
                {loginAs && (
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onLogoutAs}
                        size="small"
                        sx={{
                            mx: 2.5,
                            width: 'auto',
                            borderColor: C.textMuted,
                            color: C.textMuted,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            borderRadius: '6px',
                            '&:hover': { borderColor: C.text, color: C.text, backgroundColor: C.bgHover },
                        }}
                    >
                        {t('welcomeBar.exitActingMode', 'Exit Acting Mode')}
                    </Button>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        '& > div': {
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1.25,
                        },
                        '& .MuiButton-root': {
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            color: C.linkBlue,
                            fontSize: '0.95rem',
                            fontWeight: 400,
                            letterSpacing: 0,
                            textTransform: 'uppercase',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#0d47a1',
                            },
                        },
                    }}
                >
                    <LanguageSwitcher />
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    onClick={onLogout}
                    size="small"
                    sx={{
                        mx: 2.5,
                        width: 'auto',
                        minHeight: 44,
                        borderRadius: '4px',
                        fontSize: '0.92rem',
                        fontWeight: 400,
                        background: C.logoutBg,
                        color: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                        '&:hover': {
                            background: C.logoutHover,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                        },
                    }}
                >
                    {t('welcomeBar.signout', 'Log out')}
                </Button>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
